import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import IconBlock from 'component/common/block/icon-block';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import { useNavigate } from 'react-router-dom';
import {
  createClassroomPath,
  createSpeakingPath,
  createClipPath,
  createHomePath,
  createHubPath,
  createTestPath,
  createClozePath,
  createVocabularyBookPath,
  IYAGIGO_PATH,
} from '@constant/iyagigo/route-path';
import { IIyagigoMenu } from '@datatype/iyagigo/type';
import TextBlock from '@component/common/block/text-block';
import * as text from '@constant/text';
import { IS_PROBLEM_SERVICE } from '@constant/variable';
import BottomContainer from '@component/common/architecture/bottom-container';

const TabItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 0 0;
`;

const BottomTab: React.FC<any> = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const bottomTabMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.mainMenu) ?? null;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const playSound = usePlaySound();
  const isClassroomVisible = studyLanguage === EApiLanguageCode.JAPANESE || studyLanguage === EApiLanguageCode.ENGLISH;

  const createIconDesign = (menu: IIyagigoMenu['main']) => ({
    icon: {
      kind: 'outlined' as const,
      size: 'medium' as const,
      color: bottomTabMenu === menu ? color.BLACK_2 : color.GRAY_1,
    },
    box: {
      width: '70px',
      height: '30px',
      borderRadius: size.BORDER_RADIUS_3,
      backgroundColor: bottomTabMenu === menu ? color.GRAY_1 : 'none',
    },
  });

  const createTextDesign = (menu: IIyagigoMenu['main']) => ({
    text: { size: 'small' as const, color: bottomTabMenu === menu ? color.BLACK_2 : color.GRAY_1 },
    box: { event: { cursor: 'pointer' } },
  });

  /**
   * bottom tab 클릭 -> routing
   */
  const handleMainMenuClick = (mainMenu: IIyagigoMenu['main']) => {
    switch (mainMenu) {
      case 'begin':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('begin'));
        // navigate(IYAGIGO_PATH.SETTING_PATH);
        break;
      case 'home':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('home'));
        navigate(createHomePath());
        break;
      case 'classroom':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('classroom'));
        navigate(createClassroomPath('home'));
        break;
      case 'speaking':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('speaking'));
        navigate(createSpeakingPath());
        break;
      case 'clip':
        dispatch(navigationActions.storeIyagigoMainMenu('clip'));
        navigate(createClipPath('home'));
        break;
      case 'cloze':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('cloze'));
        navigate(createClozePath('home'));
        break;
      case 'test':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('test'));
        navigate(createTestPath('home'));
        break;
      case 'vocabulary-book':
        // playSound('tap');
        dispatch(navigationActions.storeIyagigoMainMenu('vocabulary-book'));
        navigate(createVocabularyBookPath('home'));
        break;
      // studio(학습관리)는 하단탭에서 제거 — 모바일 사이드탭으로 진입
      // case 'studio':
      // case 'hub':
      //   dispatch(navigationActions.storeIyagigoMainMenu('hub'));
      //   navigate(createHubPath());
      //   break;
      default:
    }
  };

  return (
    // <BottomContainer height={size.MOBILE_BOTTOM_HEIGHT} backgroundColor={color.GRAY_4}>
    <BottomContainer
      height={size.MOBILE_BOTTOM_HEIGHT}
      backgroundColor={color.WHITE_1}
      boxShadow={color.BOTTOM_TAB_SHADOW}>
      <TabItemContainer>
        <IconBlock
          icon='home'
          design={{
            icon: {
              kind: 'outlined',
              size: 'medium',
              color: bottomTabMenu === 'home' ? color.BLACK_2 : color.GRAY_1,
            },
            box: {
              width: '70px',
              height: '30px',
              borderRadius: size.BORDER_RADIUS_3,
              backgroundColor: 'none',
            },
          }}
          click={{ handler: () => handleMainMenuClick('home') }}
        />
        <TextBlock
          text={'홈'}
          design={{
            text: { size: 'small', color: bottomTabMenu === 'home' ? color.BLACK_2 : color.GRAY_1 },
            box: { event: { cursor: 'pointer' } },
          }}
        />
      </TabItemContainer>
      {/* 영어 교실은 기본표현·생활회화를 노출 */}
      {isClassroomVisible && (
        <TabItemContainer>
          <IconBlock
            icon='edit-note'
            design={{
              icon: {
                kind: 'outlined',
                size: 'medium',
                color: bottomTabMenu === 'classroom' ? color.BLACK_2 : color.GRAY_1,
              },
              box: {
                width: '70px',
                height: '30px',
                borderRadius: size.BORDER_RADIUS_3,
                backgroundColor: 'none',
              },
            }}
            click={{ handler: () => handleMainMenuClick('classroom') }}
          />
          <TextBlock
            text={'교실'}
            design={{
              text: { size: 'small', color: bottomTabMenu === 'classroom' ? color.BLACK_2 : color.GRAY_1 },
              box: { event: { cursor: 'pointer' } },
            }}
          />
        </TabItemContainer>
      )}
      <TabItemContainer>
        <IconBlock
          icon='bot'
          design={{
            icon: {
              kind: 'outlined',
              size: 'medium',
              color: bottomTabMenu === 'speaking' ? color.BLACK_2 : color.GRAY_1,
            },
            box: {
              width: '70px',
              height: '30px',
              borderRadius: size.BORDER_RADIUS_3,
              backgroundColor: 'none',
            },
          }}
          click={{ handler: () => handleMainMenuClick('speaking') }}
        />
        <TextBlock
          text='AI말하기'
          design={{
            text: { size: 'small', color: bottomTabMenu === 'speaking' ? color.BLACK_2 : color.GRAY_1 },
            box: { event: { cursor: 'pointer' } },
          }}
        />
      </TabItemContainer>
      {/* 기존 Clip 메뉴 보존
        <TabItemContainer>
          <IconBlock
            icon='feed'
            design={{
              icon: {
                kind: 'outlined',
                size: 'medium',
                color: bottomTabMenu === 'clip' ? color.BLACK_2 : color.GRAY_1,
              },
              box: {
                width: '70px',
                height: '30px',
                borderRadius: size.BORDER_RADIUS_3,
                backgroundColor: 'none',
              },
            }}
            click={{ handler: () => handleMainMenuClick('clip') }}
          />
          <TextBlock
            text='표현'
            design={{
              text: {
                size: 'small',
                color: bottomTabMenu === 'clip' ? color.BLACK_2 : color.GRAY_1,
              },
              box: { event: { cursor: 'pointer' } },
            }}
          />
        </TabItemContainer>
      */}
      <TabItemContainer>
        <IconBlock
          icon='quiz'
          design={{
            icon: {
              kind: 'outlined',
              size: 'medium',
              color: bottomTabMenu === 'cloze' ? color.BLACK_2 : color.GRAY_1,
            },
            box: {
              width: '70px',
              height: '30px',
              borderRadius: size.BORDER_RADIUS_3,
              backgroundColor: 'none',
            },
          }}
          click={{ handler: () => handleMainMenuClick('cloze') }}
        />
        <TextBlock
          text={'빈칸문제'}
          design={{
            text: { size: 'small', color: bottomTabMenu === 'cloze' ? color.BLACK_2 : color.GRAY_1 },
            box: { event: { cursor: 'pointer' } },
          }}
        />
      </TabItemContainer>
      <TabItemContainer>
        <IconBlock
          icon='book'
          design={{
            icon: {
              kind: 'outlined',
              size: 'medium',
              color: bottomTabMenu === 'vocabulary-book' ? color.BLACK_2 : color.GRAY_1,
            },
            box: {
              width: '70px',
              height: '30px',
              borderRadius: size.BORDER_RADIUS_3,
              backgroundColor: 'none',
            },
          }}
          click={{ handler: () => handleMainMenuClick('vocabulary-book') }}
        />
        <TextBlock
          text='단어집'
          design={{
            text: {
              size: 'small',
              color: bottomTabMenu === 'vocabulary-book' ? color.BLACK_2 : color.GRAY_1,
            },
            box: { event: { cursor: 'pointer' } },
          }}
        />
      </TabItemContainer>
      {IS_PROBLEM_SERVICE && (
        <TabItemContainer>
          <IconBlock
            icon='edit-note'
            design={{
              icon: {
                kind: 'outlined',
                size: 'medium',
                color: bottomTabMenu === 'test' ? color.BLACK_2 : color.GRAY_1,
              },
              box: {
                width: '70px',
                height: '30px',
                borderRadius: size.BORDER_RADIUS_3,
                backgroundColor: 'none',
              },
            }}
            click={{ handler: () => handleMainMenuClick('test') }}
          />
          <TextBlock
            text={studyLanguage === EApiLanguageCode.JAPANESE ? '일본어 시험' : '영어 시험'}
            design={{
              text: { size: 'small', color: bottomTabMenu === 'test' ? color.BLACK_2 : color.GRAY_1 },
              box: { event: { cursor: 'pointer' } },
            }}
          />
        </TabItemContainer>
      )}
      {/* 학습관리(studio)는 하단탭에서 제거 — 모바일 사이드탭으로 진입 */}
    </BottomContainer>
  );
};

export default BottomTab;
